import { useEffect, useMemo, useRef, useState } from 'react';
import {
  createRecorder,
  type MicTranscript,
  type Recorder,
  type RecorderState,
  type TurnConfig,
} from '../recorder';
import { isIOS } from '../recognition/WebSpeechSTTProvider';
import type { STTError } from '../recognition/types';

interface UseRecorderOptions {
  onTranscript?: (result: MicTranscript) => void;
  onError?: (error: STTError) => void;
}

interface UseRecorderResult {
  state: RecorderState;
  /** See {@link Recorder.autoEndpoint}. */
  autoEndpoint: boolean;
  /** Begin a turn; call from the tap handler. Returns true if one started. */
  start: (config: TurnConfig) => boolean;
  stop: () => void;
  /** Whether a turn is active right now (read synchronously in tap handlers). */
  isActive: () => boolean;
  /** Cancel a turn that is still opening the mic. */
  cancelPreparing: () => void;
}

/**
 * React adapter for a {@link Recorder}: one recorder per mounted component,
 * disposed on unmount. Callbacks are read fresh on every event, so passing
 * new functions doesn't recreate the recorder.
 */
export function useRecorder(
  options: UseRecorderOptions = {}
): UseRecorderResult {
  const [state, setState] = useState<RecorderState>('idle');
  const recorderRef = useRef<Recorder | null>(null);
  const optionsRef = useRef(options);
  optionsRef.current = options;

  useEffect(() => {
    const recorder = createRecorder();
    recorderRef.current = recorder;
    recorder.on('state', setState);
    recorder.on('result', (result) =>
      optionsRef.current.onTranscript?.(result)
    );
    recorder.on('error', (error) => optionsRef.current.onError?.(error));
    return (): void => {
      recorder.dispose();
      if (recorderRef.current === recorder) recorderRef.current = null;
    };
  }, []);

  // Stable across renders, so components can list them as effect deps.
  const actions = useMemo(
    () => ({
      start: (config: TurnConfig): boolean => {
        const recorder = recorderRef.current;
        if (!recorder || recorder.state !== 'idle') return false;
        recorder.start(config);
        return recorder.state !== 'idle';
      },
      stop: (): void => recorderRef.current?.stop(),
      cancelPreparing: (): void => recorderRef.current?.cancelPreparing(),
      isActive: (): boolean =>
        (recorderRef.current?.state ?? 'idle') !== 'idle',
    }),
    []
  );

  return {
    state,
    autoEndpoint: recorderRef.current?.autoEndpoint ?? isIOS(),
    ...actions,
  };
}
